import { toast } from "react-toastify";
import {
  GENERIC_NONLABOR,
  TBAR_SPACER,
  TEAMS,
} from "../../../../global/constants";
import { assignResourceTask, smartAssignResourceTask } from "../../../../services/scheduler-services/schedulerActions";
import { setRegularToAssign } from "../../../../services/utils-services/utilsActions";
import {
  removeTimezoneOffset,
  setHours,
} from "../../../../utils/DateUtils/DateUtils";
import {
  defaultSchedulerFeatures,
  getSettings,
} from "../configs";
import {
  filterByProgress,
  filterWorkPackageSchedulerBySelectedRegulars,
  getNumberOfAssignmentForUI,
  getSchedulerMode,
  reloadWorkPackageScheduler,
  saveSettings,
  toggleSchedulerMode,
} from "../utils";
import { t } from "i18next";
import { allowFeature } from "../../../../utils/FeatureUtils.js/FeatureUtils";
import { isRowReadOnly } from "../../../../utils/taskAccess";
import { ASSIGN_ON_MLAP } from "../../../../licenseData";
import { formatDuration, isExtendedDisplay } from "../../../../utils/displayUnits";

export const workPackageViews = {
  scheduler: "scheduler",
  grid: "grid",
};

// Renderers for the core columns that the user can opt into via Settings >
// Column Settings. Keys match the column `name` strings the backend stores in
// `state.columnCustomization.lookAhead.columnsVisibleOnTooltip`. Returning null
// causes the row to be skipped, matching the Gantt's NameWithInfoBubble pattern.
const isoDate = (d) => (d ? new Date(d).toJSON().substring(0, 10) : null);
const SCHEDULER_CORE_COLUMN_RENDERERS = {
  "Labor resource": (record) => record.firstCollaborator?.name || null,
  Project: (record) => record.projectName || null,
  Name: (record) => record.name || null,
  Duration: (record) => {
    if (record.duration == null) return null;
    return isExtendedDisplay()
      ? formatDuration(record.duration)
      : `${record.duration} ${t("ganttUnits.days", "days")}`;
  },
  Start: (record) => isoDate(record.startDate),
  Finish: (record) => isoDate(record.endDate),
  Progress: (record) => (record.progress != null ? `${record.progress}%` : null),
  Deadline: (record) => isoDate(record.deadlineDate),
  "Execution datetime": (record) => isoDate(record.execDateTime),
};

const translateCoreColumnName = (columnName) => {
  const key = columnName.split(" ").map((s) => s.toLowerCase()).join("_");
  const translated = t(`global.columns.${key}`);
  return translated !== `global.columns.${key}` ? translated : columnName;
};

export const buildWorkPackageEventTooltipHtml = (data, visibility) => {
  const record = data.eventRecord;
  const coreCols = visibility?.coreColumnsShownOnTooltip ?? [];
  const customCols = visibility?.customColumnsShownOnTooltip ?? [];

  const rows = [];
  coreCols.forEach((col) => {
    const renderer = SCHEDULER_CORE_COLUMN_RENDERERS[col];
    const value = renderer ? renderer(record) : null;
    if (value !== null) {
      rows.push(`<div><b>${translateCoreColumnName(col)}</b> : ${value}</div>`);
    }
  });
  customCols.forEach((col) => {
    // Scheduler events store custom column values under `customColumns[name]`
    // (different from Gantt task records which use `customColumn-${name}` keys).
    const value = record.customColumns?.[col] ?? "";
    rows.push(`<div><b>${col}</b> : ${value}</div>`);
  });

  if (rows.length === 0) {
    return `<div>${t("taskFocus.noColumnsVisible")} <b><u><a href="/office/settings" target="_blank">${t("taskFocus.tooltip.settingsLink")}</a></u></b>.</div>`;
  }
  return `<div><b>${record.name}</b><hr>${rows.join("")}</div>`;
};

export const workPackageSchedulerFeatures = (dispatch, tooltipVisibilityRef) => {
  return {
    ...defaultSchedulerFeatures,
    id: "work-package-scheduler",
    mode: getSchedulerMode("work-package-scheduler"),
    readOnly: true,
    rowHeight: getSettings("work-package-scheduler-settings").rowHeight || 60,
    barMargin: getSettings("work-package-scheduler-settings").barMargin || 10,
    eventBodyTemplate: (data) => `
      <section class="event-content">
        <div class="left-block">
          <div class="name-label">${data.name}</div>
          <div class="project-label">${data.project}</div>
        </div>

        <div class="right-block">
          <i class="icon b-fa ${data.icon}"></i>
          <span class="workValue" id="workValue_${data.id}">
            ${data.workValue}
          </span>
        </div>
      </section>
    `,
    eventRenderer({ eventRecord, resourceRecord }) {
      let icon = "";
      if (resourceRecord.entityType === TEAMS) icon = "b-fa-user";
      if (resourceRecord.entityType === GENERIC_NONLABOR)
        icon = "b-fa-building";

      const workValue = getNumberOfAssignmentForUI(eventRecord);

      return {
        id: eventRecord.id,
        name: eventRecord.name || "",
        project: eventRecord.projectName,
        icon,
        workValue,
      };
    },
    onEventMenuBeforeShow() { },
    taskEditFeature: { disabled: true },
    eventMenuFeature: {
      items: {
        copyEvent: false,
        cutEvent: false,
        unassignEvent: false,
        splitEvent: false,
        eventEdit: false,
        assign: {
          text: t("workPackageScheduler.assign"),
          icon: "b-fa b-fa-user-check",
          hidden: !allowFeature(ASSIGN_ON_MLAP),
          onItem({ eventRecord }) {
            if (isRowReadOnly(eventRecord)) {
              toast.error(t("taskFocus.readOnlyProject"));
              return;
            }
            dispatch(setRegularToAssign({ ...eventRecord.data }));
          },
        },
        quickAssign: {
          text: t("workPackageScheduler.quickAssign"),
          icon: "b-fa b-fa-user",
          onItem({ eventRecord }) {
            if (isRowReadOnly(eventRecord)) {
              toast.error(t("taskFocus.readOnlyProject"));
              return;
            }
            if (!eventRecord.data.firstCollaborator) {
              toast.error(t("workPackageScheduler.toastMsg.noCollaboratorsToAssign"));
              return;
            }

            const payload = {
              name: eventRecord.data.name,
              resourceId: eventRecord.data.firstCollaborator.id,
              startDate: removeTimezoneOffset(
                setHours(eventRecord.data.startDate, 9)
              ),
              finishDate: removeTimezoneOffset(
                setHours(eventRecord.data.endDate, 16)
              ),
              notes: eventRecord.data.notes,
              predecessors: eventRecord.data.predecessors,
              saId: +eventRecord.data.id,
              active: false,
              parentSAName: eventRecord.data.saParentName || null,
            };
            dispatch(assignResourceTask(payload));
          },
        },
        smartAssign: {
          text: t("workPackageScheduler.smartAssign"),
          icon: "b-fa b-fa-magic",
          hidden: !allowFeature(ASSIGN_ON_MLAP),
          async onItem({ eventRecord }) {
            if (isRowReadOnly(eventRecord)) {
              toast.error(t("taskFocus.readOnlyProject"));
              return;
            }
            const genericResourceId = +Object.keys(
              eventRecord.data.ganttResourceAllocation || {}
            )[0];
            if (!genericResourceId) {
              toast.error(t("workPackageGrid.noGenericResourceForSmartAssign"));
              return;
            }
            const payload = {
              name: eventRecord.data.name,
              resourceId: genericResourceId,
              startDate: removeTimezoneOffset(
                setHours(eventRecord.data.startDate, 9)
              ),
              finishDate: removeTimezoneOffset(
                setHours(eventRecord.data.endDate, 16)
              ),
              notes: eventRecord.data.notes,
              predecessors: eventRecord.data.predecessors,
              saId: +eventRecord.data.id,
              active: false,
              parentSAName: eventRecord.data.saParentName || null,
            };
            const result = await dispatch(smartAssignResourceTask(payload));
            if (result.skipped) {
              toast.warning(
                t("workPackageGrid.smartAssignNoFeasibleStart", {
                  name: eventRecord.data.name,
                })
              );
            } else if (result.success) {
              toast.success(
                t("workPackageGrid.allSmartAssignedSuccessfully", { count: 1 })
              );
            } else {
              toast.error(t("global.errors.somethingWentWrong"));
            }
          },
        },
        goToProject: {
          text: t("global.goToProject"),
          icon: "b-fa b-fa-external-link",
          onItem({ eventRecord }) {
            const {
              projectId,
              scenarioId,
              structureId,
              name,
            } = eventRecord.data;
            window.open(
              `${window.location.origin}/process/view/gantt/${projectId}/${scenarioId}/${structureId}?selectedActivity=${name}`,
              "_blank",
              "noopener noreferrer"
            );
          },
        },
      },
      eventEditFeature: { disabled: true },
    },
    eventTooltipFeature: {
      template: (data) =>
        buildWorkPackageEventTooltipHtml(data, tooltipVisibilityRef?.current),
    },
    percentBarFeature: {
      disabled: false,
      showPercentage: false,
      valueField: "progress",
    },
  };
};

export const workPackageAdditionalButtons = (
  schedulerId,
  schedulerRef,
  projectRef,
  selectedRegularsRef,
  isFilterByRegular,
  isReady,
  dispatch,
  selectedDatesRef,
  selectedProjects,
  selectedTeams,
  shouldRegularsBeFiltered,
  setView,
  shouldHideDoneBacklogs
) => {
  return [
    TBAR_SPACER,
    {
      type: "button",
      icon: "b-fa b-fa-refresh",
      tooltip: t("workPackageScheduler.refreshingData"),
      cls: "b-tbar-element",
      onClick: async (event) => {
        event.source.disabled = true;
        event.source.byRef.iconElement.className += " fa-spin";

        await reloadWorkPackageScheduler(
          schedulerRef,
          isReady,
          shouldRegularsBeFiltered,
          selectedDatesRef,
          selectedProjects,
          selectedTeams,
          dispatch
        );

        setTimeout(() => {
          event.source.disabled = false;
          event.source.byRef.iconElement.className = "b-fa b-fa-refresh";
        }, 1000); // setTimeout to avoid spamming
      },
    },
    {
      toggleable: true,
      pressed: isFilterByRegular.current,
      icon: "b-fa b-fa-filter",
      tooltip: t("workPackageScheduler.filterBacklogOnSelection"),
      cls: "b-tbar-element",
      onClick: () => {
        filterWorkPackageSchedulerBySelectedRegulars(
          projectRef,
          selectedRegularsRef.current,
          !isFilterByRegular.current
        );
        isFilterByRegular.current = !isFilterByRegular.current;
      },
    },
    {
      toggleable: true,
      pressed: shouldHideDoneBacklogs.current,
      icon: "b-fa b-fa-eye",
      tooltip: shouldHideDoneBacklogs.current
        ? t("workPackageScheduler.showFullyAssignedBacklogs")
        : t("workPackageScheduler.hideFullyAssignedBacklogs"),
      cls: "b-tbar-element",
      onToggle: ({ pressed, source }) => {
        const eventStore = projectRef.current.instance.eventStore;
        const filterId = WorkPackageSchedulerFilters.hideCompletedTasksFilter.id;
        if (pressed) {
          eventStore.filter({
            id: filterId,
            filterBy: (record) => {
              return record.data.backlogStatus !== "DONE";
            },
          });
          shouldHideDoneBacklogs.current = true;
          if (source) {
            source.tooltip = t("workPackageScheduler.showFullyAssignedBacklogs");
          }
        } else {
          eventStore.removeFilter(filterId);
          shouldHideDoneBacklogs.current = false;
          if (source) {
            source.tooltip = t("workPackageScheduler.hideFullyAssignedBacklogs");
          }
        }
      },
    },
    // {
    //   type: "button",
    //   icon: `b-fa-solid b-fa-grip-${getSchedulerMode(schedulerId, true)}`,
    //   tooltip: `Toggle to ${getSchedulerMode(schedulerId, true)} mode`,
    //   pressed: getSchedulerMode(schedulerId) === "vertical",
    //   toggleable: true,
    //   onToggle: ({ pressed }) => {
    //     toggleSchedulerMode(schedulerId, pressed, dispatch);
    //   },
    // },
    {
      type: "button",
      icon: "b-fa-solid b-fa-table",
      ref: "swtichToGridButton",
      tooltip: t("workPackageScheduler.toggleToGridView"),
      onClick: () => setView(workPackageViews.grid),
    },
    {
      ref: "settingsButton",
      type: "button",
      icon: "b-fa-sliders-h",
      tooltip: t("global.settings.viewSettings"),
      cls: "b-tbar-element",
      menu: {
        cls: "settings-menu",
        layoutStyle: {
          flexDirection: "column",
        },
        defaults: {
          type: "slider",
          showValue: true,
        },
        items: [
          {
            text: t("global.settings.rowHeight"),
            value: getSettings(`${schedulerId}-settings`).rowHeight || 50,
            min: 30,
            max: 70,
            onAction({ value }) {
              saveSettings(schedulerId, "rowHeight", value);
            },
            onInput: ({ value }) => {
              schedulerRef.current.schedulerProInstance.rowHeight = value;
            },
          },
          {
            text: t("global.settings.barMargin"),
            value: getSettings(`${schedulerId}-settings`).barMargin || 10,
            min: 0,
            max: 10,
            onAction({ value }) {
              saveSettings(schedulerId, "barMargin", value);
            },
            onInput: ({ value }) => {
              schedulerRef.current.schedulerProInstance.barMargin = value;
            },
          },
          {
            text: t("global.settings.taskBorderRadius"),
            value: getSettings(`${schedulerId}-settings`).taskBorderRadius || 0,
            min: 0,
            max: 15,
            onAction({ value }) {
              saveSettings(schedulerId, "taskBorderRadius", value);
            },
            onInput: ({ value }) => {
              schedulerRef.current.element.style.setProperty(
                "--task-border-radius",
                `${value}px`
              );
            },
          },
        ],
      },
    },
  ];
};

export const WorkPackageSchedulerFilters = {
  hideCompletedTasksFilter: {
    id: "HideCompletedTasksFilter",
    store: "eventStore",
  },
  selectedProjectsFilter: {
    id: "selectedProjectsFilter",
    store: "eventStore",
  },
  selectedResourcesFilter: {
    id: "selectedResourcesFilter",
    store: "resourceStore",
  },
  selectedRegularsForResourcesFilter: {
    id: "selectedRegularsForResourcesFilter",
    store: "resourceStore",
  },
  selectedRegularsFilter: {
    id: "selectedRegularsFilter",
    store: "eventStore",
  },
  selectedTasksForResourcesFilter: {
    id: "selectedTasksForResourcesFilter",
    store: "resourceStore",
  },
  selectedTasksFilter: {
    id: "selectedTasksFilter",
    store: "eventStore",
  },
};
